import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import validator from 'email-validator';
import SessionStore from 'stores/SessionStore';
import { getDeviceHash } from 'utils/browser';
import { Button } from 'react-bootstrap';
import Alert from 'components/ui/Alert';
import Table from 'components/v2/Table';
import Badge from 'components/v2/Badge';
import ExternalLink from 'components/v2/ExternalLink';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import FormGroup from 'components/v2/FormGroup';
import DocumentsWebAPIUtils from 'utils/DocumentsWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import Checkbox from 'components/v2/forms/Checkbox';
import Card from 'components/v2/card';
import Pagination from 'components/v2/PaginationV2';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { INVOICE, shipmentDocTypes } from 'config/documents';
import { showToast } from 'stores/ToastStore';
const TOO_MANY_DOCUMENTS = 'TOO_MANY_DOCUMENTS';
const NO_DOCUMENTS_FOUND_ERROR = 'NO_DOCUMENTS_FOUND';
const SHIPMENTS_PER_PAGE = 20;
const types = [...shipmentDocTypes, INVOICE];
function getBadgeColor(status) {
    if (status === 'canceled') {
        return 'red';
    }
    if (status === 'closed') {
        return 'green';
    }
    return 'gray';
}
export default function ShipmentResults({ referenceNumbers, handleCancel }) {
    const [shipments, setShipments] = useState([]);
    const [searching, setSearching] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [emails, setEmails] = useState();
    const [emailsInvalid, setEmailsInvalid] = useState(false);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    useEffect(() => {
        setShipments([]);
        setSearching(true);
        setNoResults(false);
        let query = {
            reference: referenceNumbers,
            page,
            per_page: SHIPMENTS_PER_PAGE,
        };
        ShipmentWebAPIUtils.getShipmentsForExportDocs(query, (res) => {
            var _a, _b, _c;
            const results = ((_a = res.entity) === null || _a === void 0 ? void 0 : _a.items) || [];
            setShipments(results);
            setTotalPages((_b = res.entity) === null || _b === void 0 ? void 0 : _b.totalPages);
            setTotalCount((_c = res.entity) === null || _c === void 0 ? void 0 : _c.totalCount);
            if (results.length === 0) {
                setNoResults(true);
            }
            setSearching(false);
        }, (res) => {
            console.log('Error', res.body);
            setSearching(false);
            setNoResults(true);
        });
    }, [referenceNumbers, page]);
    const headers = [
        {
            field: 'shipment_id',
            label: t('SHIPMENT_ID'),
        },
        {
            field: 'status',
            label: t('STATUS'),
        },
        {
            field: 'shipper',
            label: t('SHIPPER'),
        },
        { field: 'carrier', label: t('CARRIER') },
    ];
    const columns = [
        {
            field: 'shipment_id',
            render: (shipmentId, row) => {
                return (React.createElement(ExternalLink, { href: `/shipments/${row._id}`, title: shipmentId, showIcon: true }));
            },
        },
        {
            field: 'status',
            render: (status) => {
                return React.createElement(Badge, { color: getBadgeColor(status) }, titleize(status));
            },
        },
        {
            field: 'shipper',
            render: (shipper) => {
                return (shipper && (React.createElement(ExternalLink, { href: `/users/${shipper._id}`, title: shipper.company_name, showIcon: true })));
            },
        },
        {
            field: 'carrier',
            render: (carrier) => {
                return (carrier && (React.createElement(ExternalLink, { href: `/users/${carrier._id}`, title: carrier.name, showIcon: true })));
            },
        },
    ];
    const handleTypeCheck = (type, value) => {
        let updatedTypes = [...selectedTypes];
        if (value) {
            updatedTypes.push(type);
        }
        else {
            updatedTypes.splice(selectedTypes.indexOf(type), 1);
        }
        setSelectedTypes(Array.from(new Set(updatedTypes)));
    };
    const handleEmailsChange = (value) => {
        const emails = value.replace(/' '/g, '').split(',');
        const invalidEmailAddresses = emails.filter((ea) => !validator.validate(ea));
        if ((invalidEmailAddresses === null || invalidEmailAddresses === void 0 ? void 0 : invalidEmailAddresses.length) > 0) {
            setEmailsInvalid(true);
        }
        else {
            setEmailsInvalid(false);
        }
        setEmails(value);
    };
    const handleDownload = async () => {
        var _a, _b;
        try {
            setLoading(true);
            const typesToSend = selectedTypes.length
                ? selectedTypes
                : types.map((type) => type.value);
            const deviceHash = getDeviceHash();
            const languageCodeId = SessionStore.getLanguageCodeIdPreference(deviceHash) || 'en-us';
            await DocumentsWebAPIUtils.bulkDownloadByReferenceNumbers(referenceNumbers, typesToSend, emails, languageCodeId);
            showToast(t('DOCUMENTS_SENT_VIA_EMAIL'), null, 'success');
        }
        catch (e) {
            if (((_a = e.entity) === null || _a === void 0 ? void 0 : _a.error) === NO_DOCUMENTS_FOUND_ERROR) {
                showToast(t('DOWNLOAD_DOCUMENTS_ERROR'), t('NO_DOCUMENTS_ARE_FOUND_FOR_DOWNLOAD'), 'error');
            }
            else if (((_b = e.entity) === null || _b === void 0 ? void 0 : _b.error) === TOO_MANY_DOCUMENTS) {
                showToast(t('DOWNLOAD_DOCUMENTS_ERROR'), t('TOO_MANY_DOCUMENTS_PLEASE_FORMULATE_A_MORE_PRECISE_REQUEST'), 'error');
            }
            else {
                showToast(t('DOWNLOAD_DOCUMENTS_ERROR'), t('DOWNLOAD_DOCUMENTS_ERROR'), 'error');
            }
        }
        finally {
            setLoading(false);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("h3", { className: "mb-4" }, t('SELECTED_SHIPMENTS')),
        React.createElement("div", { className: "overflow-x-auto table-wrapper max-w-xl" },
            React.createElement(Table, { headers: headers, columns: columns, data: shipments, idKey: "_id" }),
            searching && (React.createElement("div", { className: "mt-4 pb-2 flex flex-col items-center", "data-testid": "searchingMessage" },
                React.createElement(LoadingCircle, { color: "white", text: t('LOADING') }))),
            noResults && (React.createElement("div", { className: "mt-4 pb-2 text-center", "data-testid": "noResultsMessage" }, t('NO_RESULTS_TO_DISPLAY'))),
            totalPages > 1 && (React.createElement(Pagination, { pageNeighbours: 1, total: totalCount, showCount: true, page: page, pages: totalPages, perPage: SHIPMENTS_PER_PAGE, onPageClick: setPage }))),
        React.createElement(Card, { className: "mt-4 max-w-xl" },
            React.createElement("div", { "data-testid": "downloadForm" },
                React.createElement(FormGroup, { label: t('SELECT_DOCUMENT_TYPES'), className: "mb-4" },
                    React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2" }, types.map((docType) => (React.createElement(Checkbox, { key: docType.value, id: docType.value, name: docType.value, value: docType.value, checked: selectedTypes.includes(docType.value), onChange: (checked) => handleTypeCheck(docType.value, checked), disabled: loading }, docType.name))))),
                React.createElement(Label, { className: "mt-4 mb-1" }, t('EMAIL_NOTIFICATIONS')),
                React.createElement("div", { className: "mt-4 sm:w-80 w-full" },
                    React.createElement(Label, { htmlFor: "emails", className: "" }, t('EMAILS')),
                    emailsInvalid && (React.createElement(Alert, { type: "warning" }, t('VALID_EMAIL_ONLY'))),
                    React.createElement(TextField, { type: "text", name: "emails", id: "emails", value: emails || '', placeholder: t('ENTER_EMAILS_SEPARATED_BY_COMMAS'), disabled: loading, onChange: handleEmailsChange })),
                React.createElement("div", { className: "sm:flex sm:justify-end sm:space-x-4 mt-8" },
                    loading && (React.createElement("div", { "data-testid": "loadingMessage" },
                        React.createElement(LoadingCircle, { color: "white", text: t('LOADING') }))),
                    React.createElement(Button, { "data-testid": "downloadButton", bsStyle: "green", onClick: handleDownload, disabled: noResults || searching || loading || !emails || emailsInvalid }, t('EMAIL_DOCUMENTS')),
                    React.createElement(Button, { "data-testid": "cancelButton", className: "mt-4 sm:mt-0", onClick: handleCancel, disabled: loading }, t('CANCEL')))))));
}
ShipmentResults.propTypes = {
    referenceNumbers: PropTypes.arrayOf(PropTypes.string),
    handleCancel: PropTypes.func,
};
